<template>
  <QPageContainer>
    <QPage class="recruitment-candidates-list">
      <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
        <QPullToRefresh no-mouse @refresh="refreshRecruitment">
          <h3 v-if="recruitment.isExpired()" class="text-positive text-center q-ma-md">
            <span>{{ $tc("ETAB.PAGES.CANDIDATE.@CHOSEN_CANDIDATES_TITLE_PERMANENT", candidates.length) }}</span>
          </h3>
          <transition-group tag="div" name="candidate-list">
            <PermanentCandidatesCard
              v-for="candidate in candidates"
              :id="candidate.getId()"
              :key="candidate.getId() + '_current'"
              :candidate="candidate"
              :recruitment="recruitment"
              :can-ignore="recruitment.isUnlocked()"
              :can-contact="true"
              @on-contact-candidate="onContactCandidate"
              @go-to-profile="goToProfile"
              @ignore-candidate="ignoreCandidate"
              @open-report-modal="openReportModal"
            />
          </transition-group>
          <NoCandidate v-if="candidates.length === 0" :img="chosenCandidateImg" :title="$t('ETAB.PAGES.CANDIDATE.@NO_PERMANENT_CANDIDATES')" />
        </QPullToRefresh>
      </transition>
    </QPage>
    <QPageSticky v-if="$q.screen.gt.xs" class="sticky-over-layout" :offset="recruitment.canBeActivated() ? [10, -50] : [10, 8]">
      <QBtn fab icon="icon-f-rotate-cw" color="secondary" @click="refreshRecruitment" />
    </QPageSticky>
  </QPageContainer>
</template>

<script setup>
  import chosenCandidateImg from "@etab/assets/img/placeholders/candidate_chosen_placeholder.png";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    GETTER_RECRUITMENT_CANDIDATES_GET_CONTACTED,
    ACTION_RECRUITMENT_CANDIDATES_GET_CONTACTED,
    ACTION_RECRUITMENT_GET,
    ACTION_REPORT_MODAL_SET,
    ACTION_REPORT_CANDIDATE_SET,
    ACTION_REPORT_DEMAND_SET,
  } from "@etab/stores/types";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { useReportCandidateStore } from "@etab/stores/report-candidate";
  import { computed, onMounted } from "vue";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRoute } from "vue-router";
  import { useRecruitmentCandidate } from "@etab/composables";
  import { useTransition } from "@core/composables";

  import NoCandidate from "@etab/components/candidates/NoCandidate.functional.vue";
  import PermanentCandidatesCard from "@etab/components/candidates/PermanentCandidates.card.vue";

  const { transition } = useTransition();
  const { $_goToProfileFromPermanentDemand, $_scrollToPreviouslyOpenedExtra, $_ignorePermanentCandidate, $_onContactCandidate } =
    useRecruitmentCandidate();
  const route = useRoute();

  const recruitmentStore = useRecruitmentStore();
  const recruitmentCandidatesStore = useRecruitmentCandidatesStore();
  const reportCandidateStore = useReportCandidateStore();

  // Computed
  const demandId = computed(() => {
    return route.params.demandId;
  });

  const demandType = computed(() => {
    return route.params.demandType;
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const candidates = computed(() => {
    return recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_CONTACTED];
  });

  // Methods
  const openReportModal = function ({ candidate }) {
    reportCandidateStore[ACTION_REPORT_MODAL_SET](true);
    reportCandidateStore[ACTION_REPORT_CANDIDATE_SET]({ candidateId: candidate.getId(), candidateFirstname: candidate.getFirstname() });
    reportCandidateStore[ACTION_REPORT_DEMAND_SET](demandId.value);
  };

  const onContactCandidate = async function ({ candidate }) {
    $_onContactCandidate({ candidate });
  };

  const goToProfile = function ({ candidate }) {
    $_goToProfileFromPermanentDemand({ candidate });
  };

  const ignoreCandidate = async function ({ candidate }) {
    try {
      LoadingManager.show();
      await $_ignorePermanentCandidate({ candidate, demandId: demandId.value });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      // stoploading after refresh
      refreshRecruitment();
    }
  };

  const refreshRecruitment = async function (refresherDone) {
    const isFromPullToRefresh = typeof refresherDone === "function";
    try {
      if (!isFromPullToRefresh) {
        LoadingManager.show();
      }
      await Promise.all([
        recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: demandId.value, demandType: demandType.value }),
        recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_CONTACTED]({ demandId: demandId.value, type: demandType.value }),
      ]);
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
      if (isFromPullToRefresh) {
        refresherDone();
      }
    }
  };

  // Mounted
  onMounted(() => {
    $_scrollToPreviouslyOpenedExtra();
  });
</script>
